import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import AdsBanner from '@/components/feature/AdsBanner';
import { useAuth } from '@/context/AuthContext';
import EmailSection from './components/EmailSection';
import FileManager from './components/FileManager';
import { cloudPlans } from '@/mocks/cloud';

const cloudHero =
  'https://readdy.ai/api/search-image?query=Modern%20cloud%20storage%20abstract%20visualization%2C%20floating%20digital%20files%20and%20folders%20in%20cyberspace%2C%20secure%20data%20transfer%20concept%2C%20warm%20blue%20and%20golden%20glow%2C%20clean%20minimal%20dark%20background%2C%20professional%20editorial%20photography&width=1600&height=800&seq=cloud-hero-01&orientation=landscape';

const features = [
  { icon: 'ri-cloud-line', title: 'Stockage Cloud', description: "Stockez vos fichiers, images, vidéos et documents en toute sécurité. Accédez-y depuis n'importe quel appareil, n'importe où." },
  { icon: 'ri-mail-send-line', title: 'Adresse Email @worldapeiron.com', description: "Créez votre adresse email personnalisée et professionnelle sous le domaine de l'écosystème, en quelques secondes." },
  { icon: 'ri-folder-5-line', title: 'Dossiers Illimités', description: 'Créez des dossiers et sous-dossiers sans limite. Organisez, déplacez, copiez et renommez en quelques clics.' },
  { icon: 'ri-share-forward-line', title: 'Partage Sécurisé', description: 'Partagez par lien public, avec expiration, mot de passe et permissions de lecture/édition.' },
  { icon: 'ri-search-line', title: 'Recherche Instantanée', description: 'Trouvez vos fichiers par nom, extension, date ou taille. Résultats en temps réel dans tout votre espace.' },
  { icon: 'ri-history-line', title: 'Historique & Corbeille', description: 'Historique complet des actions, corbeille avec restauration, et protection contre la suppression accidentelle.' },
  { icon: 'ri-shield-keyhole-line', title: 'Chiffrement & Sécurité', description: 'Chiffrement des fichiers, vérification des permissions, antivirus, journalisation et protection contre les accès non autorisés.' },
  { icon: 'ri-upload-cloud-line', title: 'Synchronisation', description: "Synchronisation automatique entre vos appareils. Téléversement rapide avec reprise en cas d'interruption." },
  { icon: 'ri-hard-drive-3-line', title: 'Plans Évolutifs', description: 'Gratuit (5Go), Standard (50Go), Premium (200Go), Business (1To). Adaptez votre espace à vos besoins.' },
];

const related = [
  { name: 'AI', path: '/ai', icon: 'ri-brain-line' },
  { name: 'Shop', path: '/shop', icon: 'ri-shopping-bag-3-line' },
  { name: 'Learn', path: '/learn', icon: 'ri-graduation-cap-line' },
  { name: 'Work', path: '/work', icon: 'ri-briefcase-4-line' },
];

function CloudPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const scrollToWorkspace = () => {
    document.getElementById('cloud-workspace')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[500px] md:min-h-[600px] flex items-center overflow-hidden pt-20">
        <div className="absolute inset-0">
          <img src={cloudHero} alt="WorldApeiron Cloud" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/90 to-background-50/60" />
        </div>

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 py-16">
          <div className="max-w-3xl">
            <span className="inline-block text-sm font-semibold text-primary-400 uppercase tracking-wider mb-4">
              WorldApeiron Écosystème
            </span>
            <h1 className="font-heading font-bold text-3xl md:text-5xl lg:text-6xl text-foreground-50 mb-4 leading-tight">
              World<span className="text-primary-400">Cloud</span>
            </h1>
            <p className="text-lg md:text-xl text-foreground-400 mb-3">
              Hébergement. Stockage. Email.
            </p>
            <p className="text-base text-foreground-400 max-w-xl mb-8 leading-relaxed">
              WorldApeiron Cloud est votre espace de stockage sécurisé et votre messagerie dans
              l'écosystème. Stockez, partagez et synchronisez vos fichiers, et créez votre adresse
              email personnalisée @worldapeiron.com.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              {loading ? (
                <div className="px-8 py-3.5">
                  <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
                </div>
              ) : user ? (
                <button
                  onClick={scrollToWorkspace}
                  className="px-8 py-3.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors whitespace-nowrap text-sm md:text-base"
                >
                  Accéder à mon espace
                </button>
              ) : (
                <>
                  <button
                    onClick={() => navigate('/auth/register')}
                    className="px-8 py-3.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors whitespace-nowrap text-sm md:text-base"
                  >
                    Obtenir 5Go gratuit
                  </button>
                  <button
                    onClick={() => navigate('/auth/login')}
                    className="px-8 py-3.5 border border-foreground-400/50 text-foreground-50 font-semibold rounded-lg hover:bg-foreground-50/10 transition-colors whitespace-nowrap text-sm md:text-base"
                  >
                    Se connecter
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Pub */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 pt-6">
        <AdsBanner pageKey="cloud" />
      </section>

      {/* Espace personnel (connecté) */}
      {!loading && user && (
        <section id="cloud-workspace" className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="text-center mb-10 md:mb-12">
              <span className="inline-block text-sm font-semibold text-primary-400 uppercase tracking-wider mb-3">
                Mon espace
              </span>
              <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-50 mb-4">
                Votre Cloud personnel
              </h2>
              <p className="text-foreground-400 max-w-xl mx-auto">
                Créez votre adresse email et gérez vos fichiers depuis un seul endroit.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl mx-auto">
              <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                <EmailSection />
              </div>
              <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                <FileManager />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block text-sm font-semibold text-primary-400 uppercase tracking-wider mb-3">
              Fonctionnalités
            </span>
            <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-50 mb-4">
              Tout ce dont vous avez besoin
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-400/40 transition-all duration-300"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-500/10 mb-4">
                  <i className={`${feature.icon} text-2xl text-primary-400`} />
                </div>
                <h3 className="font-heading font-semibold text-lg text-foreground-50 mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-foreground-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block text-sm font-semibold text-primary-400 uppercase tracking-wider mb-3">
              Forfaits
            </span>
            <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-50 mb-4">
              Choisissez votre plan
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
            {cloudPlans.map((plan) => (
              <div
                key={plan.name}
                className={`p-6 rounded-xl border transition-all duration-300 ${
                  plan.highlighted
                    ? 'bg-primary-500/10 border-primary-400/50'
                    : 'bg-background-50 border-background-200/70'
                }`}
              >
                <h3 className="font-heading font-semibold text-lg text-foreground-50 mb-1">
                  {plan.name}
                </h3>
                <p className="font-heading font-bold text-2xl text-foreground-50 mb-1">
                  {plan.price}
                </p>
                <p className="text-sm text-primary-400 font-semibold mb-3">{plan.storage}</p>
                <p className="text-sm text-foreground-400 mb-4">{plan.description}</p>
                <ul className="space-y-2 mb-5">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-foreground-500">
                      <i className="ri-check-line text-primary-400 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigate(user ? '/cloud#cloud-workspace' : '/auth/register')}
                  className={`w-full px-4 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap text-sm ${
                    plan.highlighted
                      ? 'bg-primary-500 text-foreground-950 hover:bg-primary-400'
                      : 'border border-background-300/60 text-foreground-50 hover:bg-background-200/60'
                  }`}
                >
                  Choisir {plan.name}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Modules */}
      <section className="py-16 md:py-20 bg-background-50 border-t border-background-200/70">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-50 mb-8 text-center">
            Découvrez aussi
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            {related.map((mod) => (
              <button
                key={mod.name}
                onClick={() => navigate(mod.path)}
                className="flex items-center gap-3 px-5 py-3 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-400/40 transition-all duration-300"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10">
                  <i className={`${mod.icon} text-xl text-primary-400`} />
                </div>
                <span className="font-heading font-semibold text-sm text-foreground-50">
                  {mod.name}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default CloudPage;